import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { RxArrowTopRight } from 'react-icons/rx';
import { trackApplyButton } from '@/utils/dataLayer';

const parse = (str) => {
    const m = (str || "").match(/^[\d.,\s]*/);
    const num = parseInt((m?.[0] || "").replace(/[.,\s]/g, ""), 10) || 0;
    return { value: num, suffix: (str || "").slice((m?.[0] || "").length).trim() };
};

const StatisticsBranches = () => {
    const { t } = useTranslation();
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    const [counts, setCounts] = useState([0, 0, 0]);

    const stats = useMemo(() => [
        { ...parse(t('branches-page.statistics-stat-1-number')), label: t('branches-page.statistics-stat-1-label') },
        { ...parse(t('branches-page.statistics-stat-2-number')), label: t('branches-page.statistics-stat-2-label') },
        { ...parse(t('branches-page.statistics-stat-3-number')), label: t('branches-page.statistics-stat-3-label') },
    ], [t]);

    useEffect(() => {
        if (!ref.current) return;
        const obs = new IntersectionObserver(([e]) => e.isIntersecting && setInView(true), { threshold: 0.2 });
        obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        if (!inView) return;
        const targets = stats.map((s) => s.value);
        const start = Date.now();
        const duration = 2000;
        const id = setInterval(() => {
            const elapsed = Date.now() - start;
            const p = Math.min(elapsed / duration, 1);
            const ease = 1 - (1 - p) ** 4; // ease-out quart
            setCounts(targets.map((target) => Math.floor(ease * target)));
            if (p >= 1) clearInterval(id);
        }, 16);
        return () => clearInterval(id);
    }, [inView, stats]);

    return (
        <section ref={ref} className="w-full bg-white py-[60px] md:py-[100px]">
            <div className="max-w-[90%] mx-auto">
                <div className="flex md:flex-row flex-col items-center justify-between gap-6 lg:gap-12 mb-[32px] md:mb-[64px]">
                    <h2 className="text-[32px] md:text-[48px] lg:text-[64px] xl:text-[80px] font-[500] text-main-pink leading-tight">
                        {t('branches-page.statistics-title')}
                    </h2>
                    <div className="flex flex-col gap-6 lg:max-w-md w-full md:w-auto">
                        <Link href="/apply" onClick={() => trackApplyButton("statistics_branches")}>
                            <button className="w-full md:w-[200px] h-[48px] md:h-[56px] bg-white border border-[#1053F3] text-[#1053F3] text-[14px] md:text-[16px] font-[500] rounded-full flex items-center justify-center gap-2 hover:bg-[#1053F3] hover:text-white transition-all duration-300">
                                <span>{t('branches-page.statistics-button')}</span>
                                <RxArrowTopRight className="w-[18px] h-[18px]" />
                            </button>
                        </Link>
                    </div>
                </div>
                <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6">
                    {stats.map((stat, i) => (
                        <div key={i} className="bg-[#F5F7F9] w-full rounded-[24px] md:rounded-[32px] px-[32px] h-[90px] md:h-[190px] flex flex-col justify-center">
                            <div className="text-[16px] md:text-[36px] font-[600] text-black mb-[8px] md:mb-[12px]">
                                {counts[i].toLocaleString()}{stat.suffix}
                            </div>
                            <p className="text-[12px] md:text-[14px] lg:text-[18px] font-[400] text-[#8B8B8B] leading-relaxed">
                                {stat.label}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default StatisticsBranches;
